import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import {
  color,
  AbsoluteBox,
  Button,
  Stack,
  Typography,
  Backdrop,
  FlexBox,
  Grid,
  MuiBox
} from '@kis-technologies/kis-ui';
import { CartTable } from 'components/CartTable';
import { selectors as cartSelector, Cart } from 'services/cart';
import { selectors as configSelectors, Configuration, Messages, LanguageField } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatTitle, formatCurrency } from 'utils/stringFormat';
import { useStyles } from '../styled';
import { CartSummaryOverlayProps } from '../types';

/**
 * Overlay that shows the contents of the cart, without any of the
 * functionality like removing tickets, adding a promo, etc.
 */
export const CartSummaryOverlay = (props: CartSummaryOverlayProps) => {
  const { setShowCartSummaryOverlay } = props;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const cart = useSelector(cartSelector.selectCart) as Cart;
  const totalToCharge = useSelector(cartSelector.selectCartTotalToCharge);

  const palette = get(config, 'configuration.globalSettings.palette');
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`, t('cart.cartItemLabel'));
  const cartEnabled = get(config, `${ticketingPath}.cart.enabled`, false);
  const messages: Messages = get(config, `${ticketingPath}.cart.summary.messages`, {});
  const header = get(messages, `header.${i18n.language}`, formatTitle(t('cartSummary.orderSummary')));
  const cancelButton = get(messages, `buttons.${i18n.language}[1]`, t('commonText.back'));

  const classes = useStyles();

  const onConfirm = () => {
    setShowCartSummaryOverlay(false);
    if (cart) navigate('/ticketing/cart');
  };

  const getForwardNavButtonText = () => {
    const configuredText = get(messages, `buttons.${i18n.language}[2]`);

    if (configuredText) return configuredText;
    if (cartEnabled) return t('cartSummary.proceedToCart', { cartLabel });

    return t('commonText.next');
  };

  return (
    <>
      <Backdrop sx={{ background: 'rgba(255,255,255,0.95)' }} open>
        <Grid container className={classes.overlayContainer}>
          <Typography variant="h2">{header}</Typography>
          <Grid container direction="column" className={classes.innerContainer}>
            <MuiBox className={classes.scrollableContainer}>
              <CartTable />
            </MuiBox>
            <MuiBox className={classes.totalDivider} />
            <FlexBox flexOverride="none" direction="row" fullWidth justify="between">
              <FlexBox flexOverride="none" direction="row" justify="start" align="end">
                <Typography fontSize={34} fontWeight={600} align="left">
                  {cart.quantity ? `${formatTitle(cartItemLabel)}${t('commonText.colon')} ${cart.quantity}` : null}
                </Typography>
              </FlexBox>
              <FlexBox flexOverride="none" direction="row" justify="end" align="end">
                <FlexBox direction="column" align="end">
                  <Typography fontSize={34} fontWeight={600} align="right">
                    {`${t('commonText.total')}${t('commonText.colon')} `}
                  </Typography>
                </FlexBox>
                <FlexBox direction="column" align="end" margin="0 0 0 2">
                  <Typography fontSize={34} fontWeight={600} align="right">
                    {formatCurrency(totalToCharge, {}, languageSettings)}
                  </Typography>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </Grid>
          <AbsoluteBox bottom="-45px">
            <Stack spacing={12}>
              <Button
                data-testid="cart-summary-overlay-cancel-button"
                customSize="x-large"
                customColor={color.BLACK}
                onClick={() => setShowCartSummaryOverlay(false)}
                appearance="white"
                className={classes.cancelButton}
              >
                {cancelButton}
              </Button>

              <Button
                data-testid="cart-summary-overlay-confirm-button"
                customSize="x-large"
                sx={{
                  backgroundColor: palette?.primary,
                  borderColor: palette?.primary
                }}
                onClick={onConfirm}
                appearance="primary"
                className={classes.confirmButton}
              >
                {getForwardNavButtonText()}
              </Button>
            </Stack>
          </AbsoluteBox>
        </Grid>
      </Backdrop>
    </>
  );
};
